import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import KioskAdsManager from "@/components/KioskAdsManager";
import { toast } from "sonner";
import { TrendingUp, Users, Gift, Mail, Tag, Sparkles, Clock, Star } from "lucide-react";

const TYPE_COLORS = {
  system: "#D4AF37",
  custom: "#A78BFA",
  referral_friend: "#10B981",
  referral_credit: "#F59E0B",
};
const TYPE_LABELS = {
  system: "System",
  custom: "Event",
  referral_friend: "Referral",
  referral_credit: "Credit",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}
const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const compact = (n) => {
  const v = Number(n || 0);
  if (v >= 1000) return `$${(v / 1000).toFixed(1)}k`;
  return `$${v.toFixed(0)}`;
};

// Google + Facebook review links — once saved, every completed trip texts the
// customer the link(s) + adds review buttons to the thank-you email.
function GoogleReviewCard() {
  const [google, setGoogle] = useState("");
  const [facebook, setFacebook] = useState("");
  const [saved, setSaved] = useState({ google: "", facebook: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/marketing/google-review-setting")
      .then(r => { setGoogle(r.data.url || ""); setSaved(s => ({ ...s, google: r.data.url || "" })); })
      .catch(() => {});
    api.get("/marketing/facebook-review-setting")
      .then(r => { setFacebook(r.data.url || ""); setSaved(s => ({ ...s, facebook: r.data.url || "" })); })
      .catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const [g, f] = await Promise.all([
        api.post("/marketing/google-review-setting", { url: google.trim() }),
        api.post("/marketing/facebook-review-setting", { url: facebook.trim() }),
      ]);
      setSaved({ google: g.data.url, facebook: f.data.url });
      toast.success(g.data.url || f.data.url
        ? "Saved — customers now get review links after every completed trip"
        : "Cleared — review requests are off");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  const active = saved.google || saved.facebook;
  const dirty = google.trim() !== saved.google || facebook.trim() !== saved.facebook;

  return (
    <div className="border border-white/10 bg-[#141414] p-5 mb-8" data-testid="google-review-card">
      <div className="flex items-start gap-3">
        <Star size={18} className="text-[#D4AF37] mt-0.5"/>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-[#F5F2EB] font-medium">Review requests {active ? <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-[#10B981]">● Active</span> : <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-[#71717A]">Off</span>}</div>
          <p className="text-xs text-[#A1A1AA] mt-1 mb-3">
            Paste your Google Business Profile review link and/or your Facebook page reviews link.
            When a driver completes a trip, the customer is automatically texted the link(s) and the thank-you email gets review buttons.
          </p>
          <div className="space-y-2">
            <input
              data-testid="google-review-url-input"
              value={google}
              onChange={e => setGoogle(e.target.value)}
              placeholder="Google — https://g.page/r/…/review"
              className="w-full bg-[#0a0a0a] border border-white/10 px-3 py-2 text-sm text-[#F5F2EB] placeholder:text-[#52525B] focus:border-[#D4AF37] outline-none"
            />
            <input
              data-testid="facebook-review-url-input"
              value={facebook}
              onChange={e => setFacebook(e.target.value)}
              placeholder="Facebook — https://www.facebook.com/YourPage/reviews"
              className="w-full bg-[#0a0a0a] border border-white/10 px-3 py-2 text-sm text-[#F5F2EB] placeholder:text-[#52525B] focus:border-[#D4AF37] outline-none"
            />
            <button
              data-testid="google-review-save"
              onClick={save}
              disabled={busy || !dirty}
              className="btn-gold !py-2 !px-5 text-[11px] disabled:opacity-40"
            >{busy ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MarketingTab() {
  const [window, setWindow] = useState(90);
  const [data, setData] = useState(null);
  const [waitData, setWaitData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = (days) => {
    setLoading(true);
    Promise.all([
      api.get(`/marketing/analytics?days=${days}`),
      api.get(`/marketing/wait-charges?days=${days}`),
    ])
      .then(([m, w]) => { setData(m.data); setWaitData(w.data); })
      .catch(() => { setData(null); setWaitData(null); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(window); }, [window]);

  if (loading) return <div className="px-6 md:px-12 text-xs text-[#A1A1AA]">Loading marketing analytics…</div>;
  if (!data) return <div className="px-6 md:px-12 text-xs text-red-400">Could not load analytics.</div>;

  const t = data.totals;
  const r = data.referrals;
  const maxUses = Math.max(...(data.coupons.length ? data.coupons.map(c => c.uses) : [1]));

  return (
    <section className="pb-20 px-6 md:px-12">
      <div className="max-w-[1500px] mx-auto" data-testid="marketing-tab">
        <div className="flex justify-between items-end mb-8">
          <div>
            <h2 className="font-serif text-2xl">Marketing analytics</h2>
            <p className="text-xs text-[#A1A1AA] mt-1">Coupon usage · referral cycle · email blast ROI.</p>
          </div>
          <div className="flex gap-2">
            {[30, 90, 365].map(d => (
              <button
                key={d}
                data-testid={`window-${d}`}
                onClick={() => setWindow(d)}
                className={`px-3 py-2 text-[10px] uppercase tracking-[0.22em] border ${window === d ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/10 text-[#A1A1AA]"}`}
              >Last {d}d</button>
            ))}
          </div>
        </div>

        <GoogleReviewCard/>

        {/* Top-line KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="kpis">
          <Kpi label="Coupon attach rate" value={`${t.coupon_attach_rate_pct}%`} sub={`${t.bookings_with_coupon} of ${t.bookings_in_window} bookings`} icon={<Tag size={14}/>}/>
          <Kpi label="Revenue via coupons" value={compact(t.gross_revenue_with_coupon)} sub={money(t.gross_revenue_with_coupon)} icon={<TrendingUp size={14}/>} gold/>
          <Kpi label="Discount given" value={compact(t.total_discount_given)} sub={`${money(t.total_discount_given)} total`} icon={<Sparkles size={14}/>}/>
          <Kpi label="Friends referred" value={r.friends_acquired} sub={`${r.total_referrers} referrers`} icon={<Users size={14}/>}/>
        </div>

        {/* Coupon performance bars */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-2 border border-white/10 bg-[#0A0A0A] p-5">
            <div className="flex justify-between items-baseline mb-4">
              <div className="overline">Top coupons by uses</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A]">{data.coupons.length} active codes</div>
            </div>
            {data.coupons.length === 0 && <div className="text-xs text-[#A1A1AA] py-6 text-center">No coupons used in this window.</div>}
            <div className="space-y-2.5" data-testid="coupon-bars">
              {data.coupons.slice(0, 10).map(c => {
                const w = Math.max(4, (c.uses / maxUses) * 100);
                const color = TYPE_COLORS[c.type] || "#71717A";
                return (
                  <div key={c.code} className="grid grid-cols-12 gap-2 items-center text-xs" data-testid={`coupon-bar-${c.code}`}>
                    <div className="col-span-3 font-mono text-[#F5F2EB] truncate">{c.code}</div>
                    <div className="col-span-1">
                      <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.18em]"
                        style={{ background: `${color}25`, color, border: `1px solid ${color}55` }}>
                        {TYPE_LABELS[c.type]}
                      </span>
                    </div>
                    <div className="col-span-4 h-2 bg-white/5">
                      <div className="h-full" style={{ width: `${w}%`, background: color }}/>
                    </div>
                    <div className="col-span-1 text-right text-[#F5F2EB]">{c.uses}×</div>
                    <div className="col-span-2 text-right text-[#D4AF37]">{money(c.gross_revenue)}</div>
                    <div className="col-span-1 text-right text-[#A1A1AA]">−{money(c.discount_given)}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Referral cycle panel */}
          <div className="border border-[#10B981]/30 bg-gradient-to-br from-[#10B981]/10 to-transparent p-5">
            <div className="flex items-center gap-2 mb-4 text-[#10B981] text-xs uppercase tracking-[0.22em]">
              <Gift size={13}/> Referral cycle
            </div>
            <div className="space-y-4">
              <Row k="Total referrers" v={r.total_referrers}/>
              <Row k="Friends acquired" v={r.friends_acquired}/>
              <Row k="Credits earned" v={r.credits_unused + r.credits_used}/>
              <Row k="Credits unused" v={r.credits_unused} accent="#F59E0B"/>
              <Row k="Credits redeemed" v={r.credits_used} accent="#10B981"/>
              <div className="pt-3 border-t border-white/10">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] mb-1">Credit redemption rate</div>
                <div className="flex items-end gap-2">
                  <span className="font-serif text-3xl text-[#10B981]">{r.credit_redemption_rate_pct}%</span>
                  <span className="text-[10px] text-[#71717A] pb-1.5 uppercase tracking-[0.18em]">of friends → ride back</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Top referrers + Email blasts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border border-white/10 bg-[#0A0A0A] p-5">
            <div className="overline mb-4">Top referrers</div>
            {r.top_referrers.length === 0 && <div className="text-xs text-[#A1A1AA] py-4">No referrals yet.</div>}
            <div className="space-y-2.5" data-testid="top-referrers">
              {r.top_referrers.map(tr => (
                <div key={tr.code} className="grid grid-cols-12 gap-2 items-center text-xs py-2 border-b border-white/5 last:border-b-0">
                  <div className="col-span-4">
                    <div className="text-[#F5F2EB]">{tr.name || "—"}</div>
                    <div className="text-[#71717A] text-[10px] truncate">{tr.email}</div>
                  </div>
                  <div className="col-span-3 font-mono text-[#D4AF37] text-[11px]">{tr.code}</div>
                  <div className="col-span-2 text-center">
                    <div className="text-[#F5F2EB]">{tr.referrals_count}</div>
                    <div className="text-[9px] uppercase tracking-[0.18em] text-[#71717A]">friends</div>
                  </div>
                  <div className="col-span-3 text-right text-[#A1A1AA] text-[11px]">
                    {tr.credits_used > 0 && <span className="text-[#10B981]">{tr.credits_used} used</span>}
                    {tr.credits_unused > 0 && <span className="ml-1 text-[#F59E0B]">{tr.credits_unused} unused</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border border-white/10 bg-[#0A0A0A] p-5">
            <div className="flex items-center gap-2 mb-4 text-[#A1A1AA] text-xs uppercase tracking-[0.22em]">
              <Mail size={13}/> Email blast performance
            </div>
            {data.email_blasts.length === 0 && <div className="text-xs text-[#A1A1AA] py-4">No blasts yet.</div>}
            <div className="space-y-2.5" data-testid="email-blasts">
              {data.email_blasts.map(b => {
                const sentRate = b.attempted ? (b.sent / b.attempted * 100) : 0;
                return (
                  <div key={b.code} className="grid grid-cols-12 gap-2 items-center text-xs py-2 border-b border-white/5 last:border-b-0">
                    <div className="col-span-3 font-mono text-[#D4AF37]">{b.code}</div>
                    <div className="col-span-2 text-center">
                      <div className="text-[#F5F2EB]">{b.blasts}</div>
                      <div className="text-[9px] uppercase tracking-[0.18em] text-[#71717A]">blasts</div>
                    </div>
                    <div className="col-span-2 text-center text-[#10B981]">{b.sent}</div>
                    <div className="col-span-2 text-center text-red-400">{b.failed}</div>
                    <div className="col-span-3 text-right text-[#71717A] text-[10px]">{sentRate.toFixed(0)}% delivered · {fmtDate(b.last_sent)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Wait-charge analytics */}
        {waitData && (
          <div className="mt-8 border border-[#F59E0B]/30 bg-[#0A0A0A] p-5" data-testid="wait-analytics">
            <div className="flex justify-between items-baseline mb-4">
              <div className="flex items-center gap-2 text-[#F59E0B] text-xs uppercase tracking-[0.22em]">
                <Clock size={13}/> Wait-charge analytics
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A]">{waitData.totals.trips_with_wait} trip(s) with wait charges in last {waitData.window_days}d</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <Kpi label="Wait revenue" value={compact(waitData.totals.total_wait_revenue)} sub={money(waitData.totals.total_wait_revenue)} icon={<TrendingUp size={14}/>} gold/>
              <Kpi label="Avg wait charge" value={money(waitData.totals.avg_wait_charge)} sub="per affected trip" icon={<Clock size={14}/>}/>
              <Kpi label="Total wait min" value={`${Math.round(waitData.totals.total_wait_minutes)}m`} sub="billable minutes" icon={<Clock size={14}/>}/>
              <Kpi label="Affected trips" value={waitData.totals.trips_with_wait} sub="had overruns" icon={<Tag size={14}/>}/>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="overline mb-3">By pickup type</div>
                {waitData.by_classification.length === 0 && <div className="text-xs text-[#A1A1AA] py-4">No data yet.</div>}
                <div className="space-y-2" data-testid="wait-by-class">
                  {waitData.by_classification.map(c => (
                    <div key={c.classification} className="grid grid-cols-12 gap-2 items-center text-xs py-2 border-b border-white/5 last:border-b-0">
                      <div className="col-span-3 uppercase tracking-[0.18em] text-[#F5F2EB]">{c.classification}</div>
                      <div className="col-span-2 text-center text-[#A1A1AA]">{c.count} trips</div>
                      <div className="col-span-3 text-center text-[#F59E0B]">avg {c.avg_minutes.toFixed(0)}m</div>
                      <div className="col-span-2 text-right text-[#A1A1AA]">avg {money(c.avg_charge)}</div>
                      <div className="col-span-2 text-right text-[#D4AF37]">{money(c.revenue)}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="overline mb-3">Top problem pickup locations</div>
                {waitData.by_location.length === 0 && <div className="text-xs text-[#A1A1AA] py-4">No data yet.</div>}
                <div className="space-y-2" data-testid="wait-by-location">
                  {waitData.by_location.map((loc, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center text-xs py-2 border-b border-white/5 last:border-b-0">
                      <div className="col-span-6 text-[#F5F2EB] truncate" title={loc.address}>{loc.address}</div>
                      <div className="col-span-2 text-center text-[#A1A1AA]">{loc.count}×</div>
                      <div className="col-span-2 text-center text-[#F59E0B]">avg {loc.avg_minutes.toFixed(0)}m</div>
                      <div className="col-span-2 text-right text-[#D4AF37]">{money(loc.revenue)}</div>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-[#71717A] mt-3 leading-relaxed">
                  Locations with consistent overruns may need padded pickup times. e.g., if MKE Terminal 5 averages 12 min wait, schedule 15 min earlier next time.
                </p>
              </div>
            </div>
          </div>
        )}

        <KioskAdsManager/>
      </div>
    </section>
  );
}

function Kpi({ label, value, sub, icon, gold }) {
  return (
    <div className={`p-5 border ${gold ? "border-[#D4AF37]/40 bg-[#D4AF37]/5" : "border-white/10 bg-[#0A0A0A]"}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA] mb-2">
        {icon} {label}
      </div>
      <div className={`font-serif text-3xl ${gold ? "text-[#D4AF37]" : "text-[#F5F2EB]"}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] mt-1">{sub}</div>
    </div>
  );
}

function Row({ k, v, accent }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA]">{k}</span>
      <span className="text-lg" style={{ color: accent || "#F5F2EB" }}>{v}</span>
    </div>
  );
}
